// The "upload backfill / retry" step of the rep portal's portal_orders sync loop, pulled out of
// the React component so it can be run (and tested) with a fake save function: it finds the
// rep-created invoices that are only in this browser's cache and pushes each one to portal_orders
// through savePortalOrder, so an invoice whose first save failed reaches the database later.
//
// An invoice that carries an account discount (migration 0034) is retried with the SAME
// breakdown it was created with: the cached object's own subtotal and discount, never recomputed
// from today's customer rate, so it is never stored as a net total with no discount metadata.
// Nothing in this file touches React, localStorage or Supabase directly.

// Which cached invoices still need pushing. Every guard must pass:
//   rep-created, not pulled from the database, not already pushed from this browser, belongs to
//   this rep (namesMatch), and not already present in portal_orders (existingRefs).
export function selectInvoicesToPush({ locals, pushedSet, existingRefs, repName, namesMatch }) {
  const pushed = pushedSet || new Set();
  const refs = existingRefs || new Set();
  // The ownership guard is REQUIRED: without it a shared browser would push another rep's cached invoices. Fail closed.
  if (typeof namesMatch !== "function") {
    console.warn("[selectInvoicesToPush] namesMatch is required (rep ownership check); nothing was selected");
    return [];
  }
  return (locals || []).filter((inv) => {
    if (!inv || !inv.id) return false;
    if (!inv.repCreated) return false;
    if (inv.fromDb) return false;
    if (pushed.has(inv.id)) return false;
    if (!namesMatch(inv.rep, repName)) return false;
    if (refs.has(inv.id)) return false;
    if (refs.has(String(inv.id))) return false;
    return true;
  });
}

// The savePortalOrder arguments (without onError) for a cached invoice. subtotal / discount are
// undefined for an invoice without an account discount, so its payload is exactly what it always was.
export function buildBackfillArgs(inv, fallbackRepName) {
  return {
    orderId: inv.id,
    invoiceId: inv.id,
    userId: inv.userId || inv.user_id || null,
    customer: inv.customer,
    customerEmail: inv.customerEmail || (inv.customerInfo && inv.customerInfo.email) || "",
    total: Number(inv.total) || 0,
    status: inv.status || "pending",
    paymentTerms: inv.paymentTerms || "",
    rep: inv.rep || fallbackRepName || "",
    items: Array.isArray(inv.items) ? inv.items : [],
    source: inv.source || "rep_invoice",
    subtotal: inv.subtotal,
    discount: inv.discount,
  };
}

// Pushes each invoice with save(args). Only a VERIFIED non-null row marks the id as pushed
// (pushedSet.add); a null or a throw leaves it out so the next tick retries (CLAUDE.md Rule Zero:
// the database row is the source of truth). onDiscountColumnsMissing(inv, info) is called when a
// discounted invoice failed because migration 0034 has not been run, so the caller can put it back
// on the sync-pending banner. Never throws. Returns { pushed, failed, threw } (invoice ids).
export async function pushLocalInvoices({ toPush, pushedSet, save, repName, isMounted, onDiscountColumnsMissing }) {
  const out = { pushed: [], failed: [], threw: [] };
  const mounted = typeof isMounted === "function" ? isMounted : () => true;
  for (const inv of toPush || []) {
    if (!mounted()) break;
    try {
      const row = await save({
        ...buildBackfillArgs(inv, repName),
        onError: (info) => {
          if (info && info.kind === "discount_columns_missing" && typeof onDiscountColumnsMissing === "function") {
            try { onDiscountColumnsMissing(inv, info); }
            catch (cbErr) { console.warn("[RepPortal backfill] onDiscountColumnsMissing callback threw for", inv.id, cbErr?.message || cbErr); }
          }
        },
      });
      if (row) {
        pushedSet.add(inv.id);
        out.pushed.push(inv.id);
      } else {
        console.warn("[RepPortal backfill] savePortalOrder returned null for", inv.id, "— will retry on next sync tick");
        out.failed.push(inv.id);
      }
    } catch (e) {
      // Leave the id OUT of pushedSet so the next tick retries it.
      console.warn("[RepPortal backfill] savePortalOrder threw for", inv.id, e?.message || e, "— will retry on next sync tick");
      out.threw.push(inv.id);
    }
  }
  return out;
}
